import React, { useCallback, useEffect, useState } from 'react';
import { SlidersHorizontal, Loader2, Save, RotateCcw, Info } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Grouped so the admin sees money ceilings separately from order rate limits.
const GROUPS = [
  {
    title: 'Money ceilings (€)',
    note: 'A provider-confirmed payment above a ceiling is NOT credited — it lands in Transactions → On review for your decision.',
    fields: [
      ['single_recharge_review_eur', 'Max single top-up', 'Above this, the payment is held for review.'],
      ['balance_cap_eur', 'Max wallet balance', 'A top-up that would push the balance over this is held (partially).'],
      ['new_client_recharge_eur', 'Max single top-up — new clients', 'Applies while the client is not Trusted.'],
      ['recharge_alert_eur', 'E-mail alert from', 'Credited normally, but you get a notification.'],
    ],
  },
  {
    title: 'Order rate limits',
    note: 'Counted per customer. "Trusted" clients (flag in the customer card, or a real paid order) get the higher caps.',
    fields: [
      ['orders_new_per_10min', 'New client — orders / 10 min', ''],
      ['orders_new_per_day', 'New client — orders / 24 h', ''],
      ['orders_trusted_per_10min', 'Trusted — orders / 10 min', ''],
      ['origfiles_new_per_day', 'New client — original files / 24 h', ''],
      ['origfiles_trusted_per_day', 'Trusted — original files / 24 h', ''],
    ],
  },
  {
    title: 'Trust & alerts',
    note: '',
    fields: [
      ['new_account_days', 'Account is "new" for (days)', 'Below this age a client is never auto-trusted.'],
      ['new_client_days', 'New-client top-up limit applies for (days)', ''],
      ['alert_email', 'Security alert e-mail', 'Where hold / large-payment notifications are sent.'],
    ],
  },
];

export default function RiskLimitsPanel({ token }) {
  const [draft, setDraft] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/risk/limits`, { headers: { Authorization: `Bearer ${token}` } });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      setDraft(j.limits); setDefaults(j.defaults || {});
      setMeta({ updated_by: j.updated_by, updated_at: j.updated_at });
    } catch (e) {
      toast.error(e.message || 'Could not load limits');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/risk/limits`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(draft),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      setDraft(j.limits);
      toast.success('Limits saved — they apply to the next payment / order immediately');
      load();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm('Reset every limit back to the built-in defaults?')) return;
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/risk/limits/reset`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      setDraft(j.limits);
      toast.success('Reset to defaults');
    } catch (e) {
      toast.error(e.message || 'Reset failed');
    } finally { setSaving(false); }
  };

  if (loading || !draft) {
    return <div className="py-12 flex justify-center" data-testid="risk-limits-loading"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-6" data-testid="risk-limits-panel">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <SlidersHorizontal className="w-5 h-5 text-cyan-600" />
        <h3 className="text-base md:text-lg font-bold text-slate-900">Limits &amp; anti-abuse</h3>
        <div className="ml-auto flex gap-2">
          <button
            onClick={reset} disabled={saving}
            data-testid="risk-limits-reset"
            className="px-3 py-1.5 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-medium flex items-center gap-1.5 disabled:opacity-50"
          ><RotateCcw className="w-4 h-4" /> Defaults</button>
          <button
            onClick={save} disabled={saving}
            data-testid="risk-limits-save"
            className="px-4 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm font-bold flex items-center gap-1.5 disabled:opacity-50"
          >{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</button>
        </div>
      </div>

      <div className="flex items-start gap-2 p-3 mb-5 rounded-lg bg-sky-50 border border-sky-200 text-[13px] text-sky-900">
        <Info className="w-4 h-4 mt-0.5 shrink-0" />
        <p>
          Changes take effect immediately — no deploy. A single customer can get their own
          ceiling in the customer card (<strong>Permissions &amp; limits → Individual recharge /
          balance cap</strong>), which overrides the two values below.
        </p>
      </div>

      {GROUPS.map((group) => (
        <div key={group.title} className="mb-6" data-testid={`risk-limits-group-${group.title.split(' ')[0].toLowerCase()}`}>
          <h4 className="text-sm font-bold text-slate-800">{group.title}</h4>
          {group.note && <p className="text-xs text-slate-500 mt-0.5 mb-3">{group.note}</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {group.fields.map(([key, label, hint]) => (
              <div key={key}>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">{label}</label>
                <input
                  type={key === 'alert_email' ? 'email' : 'number'}
                  min={key === 'alert_email' ? undefined : 0}
                  step={key.endsWith('_eur') ? '0.01' : '1'}
                  value={draft[key] ?? ''}
                  onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                  data-testid={`risk-limit-${key}`}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 tabular-nums"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  {hint} <span className="text-slate-400">Default: {String(defaults[key])}</span>
                </p>
              </div>
            ))}
          </div>
        </div>
      ))}

      {meta.updated_at && (
        <p className="text-[11px] text-slate-400" data-testid="risk-limits-meta">
          Last changed by <strong>{meta.updated_by || '—'}</strong> on {new Date(meta.updated_at).toLocaleString()}
        </p>
      )}
    </div>
  );
}
